import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { Button, Spinner, Tabs } from "@/src/common/components/ui";
import { buildCommunityMembersRoute, buildCommunityMobileAppRoute, STAFF_ROUTES } from "@/src/staff/constants/staff-routes";
import { type ListStaffCommunitiesResponse, useListStaffCommunitiesQuery } from "@/src/staff/services/staff-http.service";

type StaffCommunity = ListStaffCommunitiesResponse["communities"][number];

export interface CommunityDetailOutletContext {
	community: StaffCommunity;
}

const TAB_MEMBERS = "members";
const TAB_MOBILE_APP = "mobile-app";

export function CommunityDetailLayout() {
	const { t } = useTranslation("staff");
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { communityId } = useParams<{ communityId: string }>();

	const { data, isLoading } = useListStaffCommunitiesQuery();
	const community = data?.communities.find((c) => c.id === communityId);

	const selectedKey = pathname.endsWith(`/${TAB_MOBILE_APP}`) ? TAB_MOBILE_APP : TAB_MEMBERS;

	const handleTabChange = (key: string | number) => {
		if (!communityId) return;
		const target = key === TAB_MOBILE_APP ? buildCommunityMobileAppRoute(communityId) : buildCommunityMembersRoute(communityId);
		navigate(target);
	};

	if (isLoading || !community) {
		return (
			<div className="flex min-h-[50vh] items-center justify-center">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="flex-1 space-y-4 p-4">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="sm" onPress={() => navigate(STAFF_ROUTES.COMMUNITIES)}>
					<ChevronLeft size={16} />
					{t("back_to_list")}
				</Button>
				<span className="text-sm text-foreground/60">/ {community.name}</span>
			</div>

			<Tabs selectedKey={selectedKey} onSelectionChange={handleTabChange}>
				<Tabs.ListContainer>
					<Tabs.List aria-label={t("community_detail_tabs")}>
						<Tabs.Tab id={TAB_MEMBERS}>
							{t("tabs.members")}
							<Tabs.Indicator />
						</Tabs.Tab>
						<Tabs.Tab id={TAB_MOBILE_APP}>
							<Tabs.Separator />
							{t("tabs.mobile_app")}
							<Tabs.Indicator />
						</Tabs.Tab>
					</Tabs.List>
				</Tabs.ListContainer>
			</Tabs>

			<Outlet context={{ community } satisfies CommunityDetailOutletContext} />
		</div>
	);
}
